import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  Divider,
} from "@mui/material";
import { updateReportResult } from "../../../services/pathLabTestReportService";
import { updatePatientPathologyResult } from "../../../services/PatientPathologyService";
import { recordSampleResultEntered } from "../../../services/sampleTransactionService";
import { assertPathBomStockForResultEntry } from "../../../services/pathBomInventoryGate";
import PathBomInsufficientStockDialog from "../../../components/PathBomInsufficientStockDialog";
import {
  buildCompactResultPayload,
  filterSubCategoryResultsForTest,
  findReportForOpdLabTest,
  getLabTestCatalogMaps,
  getLabTestName,
  getOpdLabTestStatus,
  getSubCategoriesForTest,
  hasSubCategories,
  isOpdResultEditable,
  normalizeTestName,
  parseResultForEntryForm,
  resolveCanonicalTestNameForEntry,
  resolveOpdTestEntry,
  resolveAuthoritativeCatalogId,
  resolveSubmitReportId,
  reportMatchesLabTest,
} from "../labTestWorkflowUtils";
import { getBoundPathLabReportId, getLabTestCatalogId, getPathLabReportId } from "../../../utils/labTestDedupeUtils";

const buildInitialFormState = (labTest, report, testName) => {
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];
  const existingResult = report?.result || labTest?.result || "";
  const parsed = parseResultForEntryForm(existingResult);

  if (subCategories.length > 0) {
    const subCategoryResults = {};
    subCategories.forEach((sub) => {
      subCategoryResults[sub.name] = "";
    });
    const filtered = filterSubCategoryResultsForTest(
      testName,
      parsed.subCategoryResults || {}
    );
    Object.assign(subCategoryResults, filtered);
    const hasMatchingValues = Object.values(filtered).some(
      (value) => value && String(value).trim() !== ""
    );
    return {
      result: "",
      remarks: hasMatchingValues ? parsed.remarks || "" : "",
      subCategoryResults,
    };
  }

  return {
    result: parsed.result || "",
    remarks: parsed.remarks || "",
    subCategoryResults: {},
  };
};

const OpdTestResultFields = ({ testName, formData, onChange }) => {
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];
  const { result, remarks, subCategoryResults } = formData;

  const handleSubCategoryChange = (name, value) => {
    onChange({
      ...formData,
      subCategoryResults: { ...subCategoryResults, [name]: value },
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {testName}
      </Typography>

      {subCategories.length > 0 ? (
        <Box>
          {subCategories.map((subCategory) => (
            <Grid container spacing={1.5} key={subCategory.name} alignItems="center" sx={{ mb: 1 }}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" fontWeight="bold">
                  {subCategory.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ref: {subCategory.referenceValue} ({subCategory.unit})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Result"
                  value={subCategoryResults[subCategory.name] || ""}
                  onChange={(e) => handleSubCategoryChange(subCategory.name, e.target.value)}
                />
              </Grid>
            </Grid>
          ))}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Remarks"
            value={remarks}
            onChange={(e) => onChange({ ...formData, remarks: e.target.value })}
            sx={{ mt: 1 }}
          />
        </Box>
      ) : (
        <TextField
          fullWidth
          label="Test Result"
          value={result}
          onChange={(e) => onChange({ ...formData, result: e.target.value })}
        />
      )}
    </Paper>
  );
};

const validateFormData = (testName, formData) => {
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];

  if (subCategories.length) {
    const allowed = new Set(subCategories.map((sub) => sub.name));
    const subResults = formData.subCategoryResults || {};
    const hasAllowedValue = Object.entries(subResults).some(
      ([key, value]) => allowed.has(key) && value && value.trim() !== ""
    );
    if (!hasAllowedValue) {
      return `Please enter at least one result for ${testName}.`;
    }
    const invalidKeys = Object.keys(subResults).filter(
      (key) => subResults[key]?.trim() && !allowed.has(key)
    );
    if (invalidKeys.length) {
      return `Invalid result fields [${invalidKeys.join(", ")}] for ${testName}. Please refresh and try again.`;
    }
    return null;
  }

  if (!formData.result?.trim()) {
    return `Please enter a result for ${testName}.`;
  }
  return null;
};

const buildFinalResult = (testName, formData) => {
  const subCategories = hasSubCategories(testName) ? getSubCategoriesForTest(testName) : [];
  if (subCategories.length) {
    return buildCompactResultPayload(formData.subCategoryResults, formData.remarks, testName);
  }
  return JSON.stringify({ result: formData.result.trim() });
};

const validateFormSubCategoryKeys = (testName, formData) => {
  if (!hasSubCategories(testName)) return null;
  const allowed = new Set(getSubCategoriesForTest(testName).map((sub) => sub.name));
  const invalid = Object.keys(formData.subCategoryResults || {}).filter(
    (key) => formData.subCategoryResults[key]?.trim() && !allowed.has(key)
  );
  if (!invalid.length) return null;
  return `Result fields [${invalid.join(", ")}] do not belong to ${testName}. Please verify and submit again.`;
};

const isVirtualPrescriptionId = (value) =>
  typeof value === "string" && value.trim().toLowerCase().startsWith("virtual_");

const OpdTestResultsPanel = ({
  prescription,
  reports,
  onSubmitted,
  embedded = false,
  onSubmittingChange,
  focusTestName,
  focusReportId,
  focusLabTestId,
}) => {
  const [formState, setFormState] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockChecks, setStockChecks] = useState([]);
  const [catalogMaps, setCatalogMaps] = useState({
    catalogById: new Map(),
    catalogByName: new Map(),
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const maps = await getLabTestCatalogMaps();
      if (!cancelled) setCatalogMaps(maps);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const editableTests = useMemo(() => {
    const tests = (prescription?.patientLabTests || []).filter(isOpdResultEditable);
    const hasFocus =
      focusTestName || focusReportId != null || focusLabTestId != null;
    if (!hasFocus) return tests;

    // When editing a specific table report row, prefer that reportId; else one name/catalog match.
    if (focusReportId != null && focusReportId !== "") {
      const byReportId = tests.filter((test) => {
        const reportId = getBoundPathLabReportId(test);
        return reportId != null && Number(reportId) === Number(focusReportId);
      });
      if (byReportId.length > 0) return byReportId;

      const byNameOrCatalog = tests.filter((test) => {
        if (focusTestName) {
          const nameMatches =
            normalizeTestName(getLabTestName(test)) === normalizeTestName(focusTestName);
          if (nameMatches) return true;
        }
        if (focusLabTestId != null && focusLabTestId !== "") {
          const catalogId = getLabTestCatalogId(test);
          if (catalogId != null && Number(catalogId) === Number(focusLabTestId)) {
            // Catalog match only when name is missing or agrees.
            if (
              !focusTestName ||
              normalizeTestName(getLabTestName(test)) === normalizeTestName(focusTestName)
            ) {
              return true;
            }
          }
        }
        return false;
      });

      const unbound = byNameOrCatalog.filter((test) => {
        const reportId = getBoundPathLabReportId(test);
        return reportId == null || Number(reportId) === Number(focusReportId);
      });
      if (unbound.length > 0) return unbound.slice(0, 1);
      // Same test, different date stamp: open name-matched template; panel rebinds
      // only when focus report identity matches (prevents cross-test writes).
      if (byNameOrCatalog.length > 0 && focusTestName) {
        return byNameOrCatalog.slice(0, 1);
      }
      return [];
    }

    const focused = tests.filter((test) => {
      if (focusLabTestId != null && focusLabTestId !== "") {
        const catalogId = getLabTestCatalogId(test);
        if (catalogId != null && Number(catalogId) === Number(focusLabTestId)) return true;
      }
      if (focusTestName) {
        const target = normalizeTestName(focusTestName);
        return normalizeTestName(getLabTestName(test)) === target;
      }
      return false;
    });

    return focused.length > 0 ? focused : tests;
  }, [prescription?.patientLabTests, focusTestName, focusReportId, focusLabTestId]);

  const testEntries = useMemo(() => {
    const { catalogById, catalogByName } = catalogMaps;
    const patientId = prescription?.patientId;
    const scopedReports = (Array.isArray(reports) ? reports : []).filter((report) => {
      if (patientId == null) return true;
      const reportPatientId = report?.patientId ?? report?.patient_id;
      if (reportPatientId == null) return true;
      return Number(reportPatientId) === Number(patientId);
    });
    return editableTests.map((labTest) => {
      const focusReport =
        focusReportId != null && focusReportId !== ""
          ? scopedReports.find(
              (report) => Number(getPathLabReportId(report)) === Number(focusReportId)
            ) || null
          : null;

      // Only stamp focusReportId when the focused report identity matches this lab test.
      // Never bind via focusTestName alone — that reintroduced Typhoid→Urine overwrites.
      const canBindFocusReport =
        focusReport != null && reportMatchesLabTest(focusReport, labTest);

      const labTestForEntry = {
        ...labTest,
        __entity: "patientLabTest",
        patientId: labTest.patientId ?? patientId,
        reportId:
          canBindFocusReport && focusReportId != null && focusReportId !== ""
            ? focusReportId
            : getBoundPathLabReportId(labTest),
      };
      const report = findReportForOpdLabTest(
        scopedReports,
        prescription,
        labTestForEntry,
        catalogByName,
        catalogById,
        canBindFocusReport ? focusReportId : null
      );
      return resolveOpdTestEntry(labTestForEntry, report, catalogById, catalogByName);
    });
  }, [
    editableTests,
    reports,
    prescription,
    catalogMaps,
    focusReportId,
    focusTestName,
  ]);

  useEffect(() => {
    setFormState((prev) => {
      const nextState = {};
      testEntries.forEach(({ labTest, report, formKey }) => {
        const testName = resolveCanonicalTestNameForEntry(
          labTest,
          report,
          catalogMaps.catalogById,
          catalogMaps.catalogByName
        );
        const boundReportId = getPathLabReportId(report);
        const metaKey = `${formKey}__rid`;
        // Keep in-progress edits only for the same reportId (not another date of same test).
        if (
          prev[formKey] &&
          boundReportId != null &&
          prev[metaKey] != null &&
          Number(prev[metaKey]) === Number(boundReportId)
        ) {
          nextState[formKey] = prev[formKey];
          nextState[metaKey] = boundReportId;
        } else {
          nextState[formKey] = buildInitialFormState(labTest, report, testName);
          nextState[metaKey] = boundReportId;
        }
      });
      return nextState;
    });
  }, [testEntries, catalogMaps.catalogById, catalogMaps.catalogByName]);

  const handleSubmitAll = async () => {
    if (submitting || testEntries.length === 0) return;

    for (const { labTest, report, formKey } of testEntries) {
      const testName = resolveCanonicalTestNameForEntry(
        labTest,
        report,
        catalogMaps.catalogById,
        catalogMaps.catalogByName
      );
      const entryFormData = formState[formKey] || {};
      const error = validateFormData(testName, entryFormData);
      if (error) {
        alert(error);
        return;
      }
      const keyError = validateFormSubCategoryKeys(testName, entryFormData);
      if (keyError) {
        alert(keyError);
        return;
      }
    }

    for (const { labTest, report } of testEntries) {
      const reportId = resolveSubmitReportId(
        labTest,
        report,
        reports,
        prescription,
        catalogMaps.catalogByName,
        catalogMaps.catalogById,
        focusReportId
      );
      if (!reportId) {
        alert(`Report not found for ${getLabTestName(labTest)}. Please refresh and try again.`);
        return;
      }
    }

    setSubmitting(true);
    onSubmittingChange && onSubmittingChange(true);

    const submittedReportIds = new Set();
    const savedUpdates = [];

    try {
      // Inventory is deducted at sample collection — do not deduct again here.
      const testsNeedingStockGate = testEntries
        .filter(({ labTest }) => getOpdLabTestStatus(labTest) !== "COMPLETE")
        .map(({ labTest, report }) => {
          const authoritativeLabTestId = resolveAuthoritativeCatalogId(
            labTest,
            report,
            catalogMaps.catalogByName,
            catalogMaps.catalogById
          );
          return {
            ...labTest,
            labTestId: authoritativeLabTestId || labTest.labTestId || labTest.testId,
            testId: authoritativeLabTestId || labTest.labTestId || labTest.testId,
            testName: getLabTestName(labTest),
          };
        });

      if (testsNeedingStockGate.length > 0) {
        const alreadyCollected = testsNeedingStockGate.every(
          (labTest) => getOpdLabTestStatus(labTest) === "COLLECTED"
        );
        const stockGate = await assertPathBomStockForResultEntry({
          patientLabTests: testsNeedingStockGate,
          alreadyCollected,
        });
        if (!stockGate.ok) {
          setStockChecks(stockGate.checks || []);
          setStockDialogOpen(true);
          return;
        }
      }

      for (const { labTest, report, formKey, catalogId } of testEntries) {
        const reportId = resolveSubmitReportId(
          labTest,
          report,
          reports,
          prescription,
          catalogMaps.catalogByName,
          catalogMaps.catalogById,
          focusReportId
        );
        if (submittedReportIds.has(reportId)) {
          alert(
            `Multiple tests resolved to the same report (ID ${reportId}) for ${getLabTestName(labTest)}. Please refresh and try again, or edit tests individually.`
          );
          return;
        }
        submittedReportIds.add(reportId);
        const testName = resolveCanonicalTestNameForEntry(
          labTest,
          report,
          catalogMaps.catalogById,
          catalogMaps.catalogByName
        );
        const entryFormData = formState[formKey] || {};
        const finalResult = buildFinalResult(testName, entryFormData);
        const keyError = validateFormSubCategoryKeys(testName, entryFormData);
        if (keyError) {
          alert(keyError);
          return;
        }
        const authoritativeLabTestId = resolveAuthoritativeCatalogId(
          labTest,
          report,
          catalogMaps.catalogByName,
          catalogMaps.catalogById
        );
        if (!authoritativeLabTestId) {
          alert(`Lab test ID not found for ${getLabTestName(labTest)}. Please refresh and try again.`);
          return;
        }

        const boundReport =
          (Array.isArray(reports) ? reports : []).find(
            (row) => Number(getPathLabReportId(row)) === Number(reportId)
          ) || report;
        if (boundReport && !reportMatchesLabTest(boundReport, labTest)) {
          const reportName =
            boundReport?.testName || boundReport?.test_name || boundReport?.name || "";
          alert(
            `Refusing to save ${getLabTestName(labTest)} onto report "${reportName}" (ID ${reportId}). Please refresh and try again.`
          );
          return;
        }

        const prescriptionId = prescription?.id;
        const sampleMeta = {
          patientId: prescription?.patientId ?? prescription?.patient_id,
          patientName: prescription?.patientName ?? prescription?.patient_name,
          labTestId: authoritativeLabTestId,
          labTestName: testName,
          prescriptionId: isVirtualPrescriptionId(prescriptionId) ? null : prescriptionId,
          invoiceId: prescription?.invoiceId ?? prescription?.invoice_id,
          patientType: "opd",
          billed: true,
        };

        // Result API body stays { result, resultStatus } only.
        // Sample sync runs after 200 via pathLabTestReportService (+ explicit upsert below).
        await updateReportResult(reportId, {
          result: finalResult,
          resultStatus: "COMPLETE",
          _sampleTransactionMeta: sampleMeta,
        });
        savedUpdates.push({
          reportId,
          result: finalResult,
          resultStatus: "COMPLETE",
          patientId: prescription?.patientId ?? null,
          testName,
        });

        await recordSampleResultEntered({
          ...sampleMeta,
          reportId,
          sampleCollected: true,
          resultEntered: "YES",
          result: "YES",
        });

        if (
          prescriptionId &&
          !isVirtualPrescriptionId(prescriptionId) &&
          authoritativeLabTestId &&
          boundReport &&
          reportMatchesLabTest(boundReport, labTest)
        ) {
          await updatePatientPathologyResult(
            prescription.patientId,
            prescriptionId,
            authoritativeLabTestId,
            finalResult,
            "COMPLETE",
            {
              reportId,
              invoiceId: prescription?.invoiceId ?? prescription?.invoice_id,
              patientType: "opd",
              labTestName: testName,
              patientName: prescription?.patientName,
            }
          );
        } else if (isVirtualPrescriptionId(prescriptionId)) {
          console.info(
            "[OpdTestResultsPanel] Skipping patient_pathology result update for virtual prescription:",
            prescriptionId
          );
        }
      }

      const savedCount = savedUpdates.length;
      const editingExisting = editableTests.every(
        (test) => getOpdLabTestStatus(test) === "COMPLETE"
      );
      const successMessage =
        editingExisting && savedCount <= 1
          ? "Result updated successfully."
          : !editingExisting && savedCount <= 1
            ? "Result saved successfully."
            : editingExisting
              ? "Results updated successfully."
              : "All test results saved successfully.";
      alert(successMessage);
      onSubmitted &&
        (await onSubmitted({
          patientId: prescription?.patientId ?? null,
          updates: savedUpdates,
        }));
    } catch (error) {
      console.error("[OpdTestResultsPanel] Failed to save results:", error);
      alert("Failed to save test results. Please try again.");
    } finally {
      setSubmitting(false);
      onSubmittingChange && onSubmittingChange(false);
    }
  };

  if (editableTests.length === 0) return null;

  const isEditMode = editableTests.every((test) => getOpdLabTestStatus(test) === "COMPLETE");
  const isSingleTest = testEntries.length <= 1;
  const helperText = isEditMode
    ? isSingleTest
      ? "Update the result below, then submit."
      : "Update the result(s) below, then submit."
    : isSingleTest
      ? "Enter the result below, then submit."
      : "Enter results for all collected tests below, then submit once.";
  const submitLabel = isEditMode
    ? isSingleTest
      ? "Update Result"
      : "Update Results"
    : isSingleTest
      ? "Submit Result"
      : "Submit All Results";

  const content = (
    <>
      {!embedded && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {isEditMode ? "Edit Results" : "Add Results"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {helperText}
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}
      {embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {helperText}
        </Typography>
      )}
      <Box sx={{ maxHeight: embedded ? 480 : 420, overflowY: "auto", pr: 1 }}>
        {testEntries.map(({ labTest, report, formKey }) => {
          const testName = resolveCanonicalTestNameForEntry(
            labTest,
            report,
            catalogMaps.catalogById,
            catalogMaps.catalogByName
          );
          return (
          <OpdTestResultFields
            key={`${prescription.id}-${formKey}`}
            testName={testName}
            formData={formState[formKey] || { result: "", remarks: "", subCategoryResults: {} }}
            onChange={(next) => setFormState((prev) => ({ ...prev, [formKey]: next }))}
          />
        );})}
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={handleSubmitAll} disabled={submitting}>
          {submitting ? "Processing..." : submitLabel}
        </Button>
      </Box>
    </>
  );

  const stockDialog = (
    <PathBomInsufficientStockDialog
      open={stockDialogOpen}
      onClose={() => setStockDialogOpen(false)}
      checks={stockChecks}
      actionLabel="add / update result"
      title="Cannot save result — consumable not in inventory"
    />
  );

  if (embedded) {
    return (
      <>
        {stockDialog}
        {content}
      </>
    );
  }

  return (
    <>
      {stockDialog}
      <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
        {content}
      </Box>
    </>
  );
};

export default OpdTestResultsPanel;
